import { useParams, Link } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { siteConfig, articles, categories } from '@/mocks/siteData';

function generateArticleContent(article: typeof articles[0]): string {
  const categoryName = article.category;
  return `
    <p>在当今数字化时代，${article.title.replace('17c com入口', '17c com入口')}已成为广大用户关注的热点话题。无论您是初次接触还是长期用户，掌握正确的访问方式都至关重要。本文将为您详细解读相关要点，助您轻松上手。</p>

    <h2>一、${categoryName}背景与重要性</h2>
    <p>随着互联网技术的不断发展，17c com入口作为连接用户与平台的重要桥梁，其稳定性和安全性直接影响着用户的使用体验。${article.summary}</p>
    <p>根据最新数据统计，每天有超过<strong>10万用户</strong>通过各类入口访问17c com平台。在这些用户中，约有15%的用户会遇到不同程度的访问问题。了解如何正确使用入口地址，可以帮您避免这些常见困扰。</p>

    <h2>二、核心要点详解</h2>
    <p>17c com入口提供了多种访问方式，以满足不同用户在不同场景下的需求。以下是几个关键点：</p>
    <ul>
      <li><strong>官方主入口</strong>：最推荐的访问方式，稳定性最高，速度最快</li>
      <li><strong>备用入口</strong>：当主入口出现波动时的可靠替代方案</li>
      <li><strong>移动端入口</strong>：针对手机端优化，支持随时随地访问</li>
      <li><strong>登录直达入口</strong>：可直接跳转至登录界面，节省操作步骤</li>
    </ul>
    <p>值得注意的是，所有官方入口地址均经过严格的安全审核，用户可放心使用。我们建议您收藏至少两个入口地址，以应对突发情况。</p>

    <h2>三、常见问题与解决方案</h2>
    <p>在使用17c com入口的过程中，用户可能会遇到以下几种常见问题：</p>
    <h3>1. 页面无法打开</h3>
    <p>这是最常见的问题之一。通常原因是网络波动、DNS解析异常或浏览器缓存过期。建议先尝试清除浏览器缓存，或更换DNS服务器为<strong>114.114.114.114</strong>。如果问题依然存在，可使用备用入口地址。</p>
    <h3>2. 访问速度慢</h3>
    <p>访问速度受多种因素影响，包括您的网络环境、设备性能以及入口服务器的负载情况。建议在网络状况良好的环境下使用有线连接访问，或尝试在不同时段访问以避开高峰期。</p>
    <h3>3. 页面跳转异常</h3>
    <p>如果您遇到页面自动跳转到错误地址的情况，可能是浏览器插件干扰或DNS被劫持。建议暂时禁用浏览器插件后重试，或重置DNS设置。</p>

    <h2>四、安全使用建议</h2>
    <p>安全始终是使用17c com入口的首要考虑因素。以下是我们为您整理的安全使用建议：</p>
    <ol>
      <li>仅通过本页面提供的官方认证入口地址访问17c com</li>
      <li>不要在来源不明的网站或链接中输入您的账号和密码</li>
      <li>开启平台提供的二次验证功能，增加账户安全层级</li>
      <li>定期更换密码，避免使用过于简单的密码组合</li>
      <li>在公共网络环境下尽量避免登录账户</li>
      <li>定期检查账户登录记录，发现异常及时处理</li>
    </ol>

    <h2>五、总结与推荐</h2>
    <p>综上所述，${article.title}并非难事，关键在于掌握正确的方法和技巧。我们建议您：</p>
    <ul>
      <li>收藏本页面，随时获取最新的17c com入口地址</li>
      <li>关注我们的${categoryName}栏目，获取最新的使用教程</li>
      <li>遇到问题时先查阅常见问答，多数问题都能找到答案</li>
      <li>如有特殊问题，可通过关于我们页面的联系方式获取帮助</li>
    </ul>
    <p>17c com入口致力于为用户提供安全、稳定、流畅的访问体验。我们将持续更新和维护入口地址，确保每一位用户都能顺利访问平台。感谢您的信任与支持！</p>
  `;
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => articles.find((a) => a.slug === slug), [slug]);
  const articleCategory = useMemo(
    () => categories.find((c) => c.slug === article?.categorySlug),
    [article],
  );

  const relatedArticles = useMemo(() => {
    if (!article) return [];
    return articles
      .filter((a) => a.categorySlug === article.categorySlug && a.slug !== article.slug)
      .slice(0, 4);
  }, [article]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100 text-secondary-500">
              <i className="ri-file-text-line text-2xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-foreground-600 text-sm mb-6">该文章不存在或已被移除</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap">
              <i className="ri-home-line"></i>返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: article.category, to: `/category/${article.categorySlug}` },
    { label: article.title },
  ];

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    datePublished: article.date,
    author: {
      '@type': 'Organization',
      name: siteConfig.brand.name,
    },
    publisher: {
      '@type': 'Organization',
      name: siteConfig.brand.name,
    },
    keywords: article.tags.join(', '),
    url: `${siteConfig.url}/article/${article.slug}`,
  };

  return (
    <>
      <PageMeta
        title={`${article.title} - ${siteConfig.name}`}
        description={article.summary}
        canonical={`${siteConfig.url}/article/${article.slug}`}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }} />

      <Navbar />
      <ScrollToTop />

      <article className="pt-20">
        <div className="w-full max-w-[900px] mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <div className="py-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          {/* Article Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-3 mb-4 text-xs text-foreground-500">
              {articleCategory && (
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 font-medium hover:bg-accent-200 transition-colors"
                >
                  {article.category}
                </Link>
              )}
              <span>{article.date}</span>
              <span>阅读 {article.readTime}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 leading-tight mb-4">
              {article.title}
            </h1>
            <p className="text-foreground-600 text-base leading-relaxed">
              {article.summary}
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {article.tags.map((tag) => (
                <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-3 py-1 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          </header>

          {/* Featured Image */}
          <div className="rounded-xl overflow-hidden mb-10 aspect-[16/9]">
            <img
              src={article.thumbnail}
              alt={article.title}
              title={article.title}
              className="w-full h-full object-cover"
              width="900"
              height="506"
            />
          </div>

          {/* Article Content */}
          <div
            className="prose-content pb-10"
            dangerouslySetInnerHTML={{ __html: generateArticleContent(article) }}
          />

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="pb-14 md:pb-20">
              <div className="border-t border-background-200 pt-10">
                <h2 className="font-heading text-xl font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group flex gap-4 p-4 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
                    >
                      <div className="w-24 h-18 flex-shrink-0 rounded-lg overflow-hidden">
                        <img
                          src={ra.thumbnail}
                          alt={ra.title}
                          className="w-full h-full object-cover"
                          width="96"
                          height="72"
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="text-xs text-foreground-500 mb-1">{ra.date}</div>
                        <h3 className="font-heading font-medium text-foreground-900 text-sm line-clamp-2 group-hover:text-primary-600 transition-colors">
                          {ra.title}
                        </h3>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </div>
      </article>

      <Footer />
    </>
  );
}